import { z } from 'zod';

import { isoDatetimeToDate } from '../codecs';
import { Player } from './player';

export interface PlayerGroupWithPlayers extends PlayerGroup {
  players: Player[];
}

/**
 * The colors a player group can be marked with.
 *
 * These are semantic names, not concrete color values, so the rendering layer stays free to map
 * them onto the design system (and onto light/dark themes). Extending the palette means adding an
 * entry here and mapping it in the webapp's player group appearance map.
 */
export const PLAYER_GROUP_COLORS = [
  'green',
  'blue',
  'purple',
  'red',
  'orange',
  'yellow',
  'lime',
  'gray',
] as const;

export type PlayerGroupColor = (typeof PLAYER_GROUP_COLORS)[number];

/** The color used for groups that were created without picking one. */
export const DEFAULT_PLAYER_GROUP_COLOR: PlayerGroupColor = 'gray';

/**
 * The icons a player group can be marked with.
 *
 * Like {@link PLAYER_GROUP_COLORS} these are semantic names that the rendering layer maps onto
 * concrete icons, so the API does not depend on the icon library in use.
 */
export const PLAYER_GROUP_ICONS = [
  'users',
  'star',
  'shield',
  'flame',
  'zap',
  'target',
  'trophy',
  'crown',
  'heart',
  'activity',
  'dumbbell',
  'stethoscope',
] as const;

export type PlayerGroupIcon = (typeof PLAYER_GROUP_ICONS)[number];

/** The icon used for groups that were created without picking one. */
export const DEFAULT_PLAYER_GROUP_ICON: PlayerGroupIcon = 'users';

/** Falls back to the default appearance so groups always render with a concrete color/icon. */
const playerGroupColorSchema = z.enum(PLAYER_GROUP_COLORS).default(DEFAULT_PLAYER_GROUP_COLOR);
const playerGroupIconSchema = z.enum(PLAYER_GROUP_ICONS).default(DEFAULT_PLAYER_GROUP_ICON);

export type PlayerGroup = z.output<typeof playerGroupSchema>;
export type PlayerGroupDTO = z.input<typeof playerGroupSchema>;

export const playerGroupSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  color: playerGroupColorSchema,
  icon: playerGroupIconSchema,
  createdAt: isoDatetimeToDate,
});

export type CreatePlayerGroupPayload = z.output<typeof createPlayerGroupPayloadSchema>;
export type CreatePlayerGroupPayloadDTO = z.input<typeof createPlayerGroupPayloadSchema>;

export const createPlayerGroupPayloadSchema = z.object({
  name: z.string().min(1),
  color: playerGroupColorSchema,
  icon: playerGroupIconSchema,
});

export type UpdatePlayerGroupPayload = z.output<typeof updatePlayerGroupPayloadSchema>;
export type UpdatePlayerGroupPayloadDTO = z.input<typeof updatePlayerGroupPayloadSchema>;

export const updatePlayerGroupPayloadSchema = z.object({
  name: z.string().min(1),
  // Optional without a default: an omitted value leaves the group's current appearance untouched,
  // whereas defaulting would reset it on every update that only changes the name.
  color: z.enum(PLAYER_GROUP_COLORS).optional(),
  icon: z.enum(PLAYER_GROUP_ICONS).optional(),
});

export const PlayerGroupUtils = {
  getPlayersOfGroups(players: Player[], groupIds: string[]): Player[] {
    return players.filter((player) =>
      groupIds.some((groupId) => player.attributes.cv_groups?.includes(groupId)),
    );
  },

  /**
   * Resolves a stored color to one the UI can render. Groups created before a color was pickable
   * have none, and a color that was removed from the palette must not break the group either.
   */
  toColor(value: string | null | undefined): PlayerGroupColor {
    return PLAYER_GROUP_COLORS.find((color) => color === value) ?? DEFAULT_PLAYER_GROUP_COLOR;
  },

  /** Resolves a stored icon to one the UI can render. See {@link PlayerGroupUtils.toColor}. */
  toIcon(value: string | null | undefined): PlayerGroupIcon {
    return PLAYER_GROUP_ICONS.find((icon) => icon === value) ?? DEFAULT_PLAYER_GROUP_ICON;
  },
};
